import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { PlanCode } from './price';

export type Interval = 'MONTHLY' | 'SIX_MONTHLY' | 'ANNUAL';
export type AddOnCode = 'CUSTOM_MEDIA' | 'TALK_BOARD';
export type AiCode = 'AI_SMALL' | 'AI_MEDIUM' | 'AI_LARGE';

export type Offer = {
  product: PlanCode | AddOnCode | AiCode;
  type: 'PLAN' | 'ADD_ON' | 'AI';
  interval: Interval;
  perLearner: boolean;
  maxLearners: number | null;
  unitAmount: number;
  currency: string;
};

export type Entitlements = { main: boolean; customMedia: boolean; talkBoard: boolean };

export type SubscriptionStatus = {
  limitedTrial?: boolean;
  status: string;
  plan: string;
  maxLearners: number;
  currentPeriodEndEpochMs: number | null;
  active: boolean;
  pendingPlan: string | null;
  pendingPlanFromEpochMs: number | null;
  pendingInterval: Interval | null;
  interval: Interval;
  mainPlan: boolean;
  addOns: AddOnCode[];
  seats: number;
  entitlements: Entitlements;
};

export const INTERVALS: { value: Interval; label: string; every: string; saving: string | null }[] = [
  { value: 'MONTHLY', label: 'Monthly', every: 'month', saving: null },
  { value: 'SIX_MONTHLY', label: 'Every 6 months', every: '6 months', saving: 'Save 10%' },
  { value: 'ANNUAL', label: 'Yearly', every: 'year', saving: 'Save 20%' },
];

export const ADD_ON_INFO: Record<AddOnCode, { name: string; describes: string; needsMain: boolean }> = {
  CUSTOM_MEDIA: {
    name: 'Custom pictures and voice',
    describes: 'Every plan includes 100 of your own photos and 100 voice recordings. This add-on gives you more: 100 MB for each place on your plan.',
    needsMain: false,
  },
  TALK_BOARD: {
    name: 'Talk Board',
    describes: 'A picture board the learner taps to speak. Works on its own or with a plan.',
    needsMain: false,
  },
};

export async function fetchOffers(): Promise<Offer[]> {
  return (await api<Offer[] | undefined>('/subscription/offers', { token: true })) ?? [];
}

export async function fetchStatus(): Promise<SubscriptionStatus> {
  return api<SubscriptionStatus>('/subscription/status', { token: true });
}

export function money(amountMinor: number, currency: string): string {
  const fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() });
  const digits = fmt.resolvedOptions().maximumFractionDigits ?? 2;
  return fmt.format(amountMinor / 10 ** digits);
}

let cached: Promise<SubscriptionStatus> | null = null;

export function refreshEntitlements() {
  cached = null;
}

export function useEntitlements(signedIn = true): Entitlements | null {
  const [value, setValue] = useState<Entitlements | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!signedIn) { setValue(null); return; }
    if (!cached) cached = fetchStatus();
    cached.then((s) => { if (!cancelled) setValue(s.entitlements ?? { main: true, customMedia: false, talkBoard: false }); }).catch(() => {
      cached = null;
      if (!cancelled) setValue({ main: true, customMedia: false, talkBoard: false });
    });
    return () => { cancelled = true; };
  }, [signedIn]);
  return value;
}
